import React, { useEffect, useState } from "react";
import styled from "styled-components";

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fcfcfc;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  box-shadow: 2px 4px 8px rgba(0, 0, 0, 0.05);
  color: #a7a8a9;
  padding: 0;
  margin: 0;
  width: 100%;
`;

const OptionSpan = styled.span`
  cursor: pointer;
  flex: 1;
  text-align: center;
  border-radius: 6px;
  line-height: 100%;
  transition: 0.2s;
  padding: 4px 0;
  margin: 1px 1px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;

  &:hover {
    cursor: pointer;
    background-color: #f0f0f0;
  }

  &.active {
    border: 1px solid #f0f0f0;
    cursor: default;
    background-color: #a7a8a9;
    color: #fff;

    /* グローバルの .material-symbols-rounded { color: light-gray } が
     * 継承色より優先されるため、選択中はアイコン色も明示的に白にする */
    .material-symbols-rounded {
      color: #fff;
    }
  }

  .material-symbols-rounded {
    font-size: 20px;
  }
`;

const IconOptionSpan = styled(OptionSpan)`
  flex: 0 0 auto;
  width: 36px;
  min-width: 36px;
`;

export interface SwitchOption<T extends string | number = string | number> {
  label?: string;
  value: T;
  icon?: string;
}

export interface SwitchComponentProps<T extends string | number = string | number> {
  defaultValue?: T;
  value?: T;
  options: SwitchOption<T>[];
  onChange: (value: T | null) => void;
  required?: boolean;
  className?: string;
}

function SwitchComponent<T extends string | number = string | number>({
  defaultValue,
  value: controlledValue,
  options,
  onChange,
  required = true,
  className = "",
}: SwitchComponentProps<T>): React.ReactElement {
  const [internalValue, setInternalValue] = useState<T | undefined>(defaultValue);

  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : internalValue;

  useEffect(() => {
    if (isControlled) {
      setInternalValue(controlledValue);
    }
  }, [controlledValue, isControlled]);

  const handleClick = (optionValue: T) => {
    if (value === optionValue && !required) {
      if (!isControlled) {
        setInternalValue(undefined);
      }
      onChange(null);
    } else {
      if (!isControlled) {
        setInternalValue(optionValue);
      }
      onChange(optionValue);
    }
  };

  return (
    <SwitchWrapper className={className}>
      {options.map((option) => {
        if (option.icon) {
          return (
            <IconOptionSpan
              key={String(option.value)}
              className={option.value === value ? "active" : ""}
              onClick={() => handleClick(option.value)}
            >
              <span className="material-symbols-rounded">{option.icon}</span>
            </IconOptionSpan>
          );
        }

        return (
          <OptionSpan
            key={String(option.value)}
            className={`bodytext ${option.value === value ? "active" : ""}`}
            onClick={() => handleClick(option.value)}
          >
            {option.label}
          </OptionSpan>
        );
      })}
    </SwitchWrapper>
  );
}

export default SwitchComponent;
